import { Fragment } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, XCircle } from 'lucide-react'
import clsx from 'clsx'
import { AppriseServerSummaryCell } from '@/components/apprise-vm/AppriseServerSummaryCell'
import { Button } from '@/components/common/Button'
import pageStyles from '@/pages/DeploymentsPage.module.css'
import type { DeploymentRequest } from '@/types'
import { formatUsd } from '@/utils/workwise/cost'
import { isActiveStatus } from '@/utils/format'
import { type ColumnDef, parseOrdering } from './columns'
import { DeploymentCell } from './DeploymentCells'
import type { CustomerGroup } from './grouping'
import styles from './deployments.module.css'

interface DeploymentsTableProps {
  /** Visible columns, in display order. */
  columns: ColumnDef[]
  rows: DeploymentRequest[]
  /** When set, rows are shown under one header row per customer. */
  groups?: CustomerGroup[]
  /** Current `?ordering=` value, e.g. "-created_at". */
  ordering: string
  onSort: (sortKey: string) => void
  onRowClick: (dep: DeploymentRequest) => void
  onView: (dep: DeploymentRequest) => void
  onCancel: (dep: DeploymentRequest) => void
  cancellingId: number | null
}

function SortHeader({ column, ordering, onSort }: { column: ColumnDef; ordering: string; onSort: (key: string) => void }) {
  if (!column.sortKey) return <th scope="col">{column.label}</th>
  const current = parseOrdering(ordering)
  const direction = current && current.key === column.sortKey ? current.direction : null
  const active = direction !== null
  const Icon = direction === null ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown
  return (
    <th scope="col" aria-sort={direction === null ? 'none' : direction === 'asc' ? 'ascending' : 'descending'}>
      <button
        type="button"
        className={styles.sortButton}
        data-testid={`sort-${column.id}`}
        onClick={() => onSort(column.sortKey as string)}
      >
        {column.label}
        <Icon size={12} className={clsx(styles.sortIcon, active && styles.sortIconActive)} />
      </button>
    </th>
  )
}

/** The deployments table: sortable headers (server-side), optional customer groups. */
export function DeploymentsTable({
  columns,
  rows,
  groups,
  ordering,
  onSort,
  onRowClick,
  onView,
  onCancel,
  cancellingId,
}: DeploymentsTableProps) {
  const colSpan = columns.length + 1

  const renderRow = (dep: DeploymentRequest) => {
    // A dispatched WorkWise run cannot be cancelled from the portal (the API answers 409),
    // so only a pending one offers Cancel. Other products keep the original rule.
    const canCancel = dep.workwise ? dep.status === 'pending' : isActiveStatus(dep.status) || dep.status === 'pending'
    return (
      <Fragment key={dep.id}>
      <tr
        className={clsx(pageStyles.tableRow, dep.server && pageStyles.hasServerRow)}
        data-testid={`deployment-row-${dep.id}`}
        onClick={() => onRowClick(dep)}
      >
        {columns.map((column) => (
          <Fragment key={column.id}>
            <DeploymentCell columnId={column.id} dep={dep} />
          </Fragment>
        ))}
        <td>
          <div className={pageStyles.actions}>
            <Button
              variant="ghost"
              size="sm"
              icon={<Eye size={14} />}
              onClick={(e) => {
                e.stopPropagation()
                onView(dep)
              }}
            >
              View
            </Button>
            {canCancel && (
              <Button
                variant="danger"
                size="sm"
                icon={<XCircle size={14} />}
                loading={cancellingId === dep.id}
                onClick={(e) => {
                  e.stopPropagation()
                  onCancel(dep)
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </td>
      </tr>
      {/* Apprise rows: server metadata (name, OS, VM size, region, IP, infra / app status) */}
      {dep.server && (
        <tr className={clsx(pageStyles.tableRow, pageStyles.serverRow)} onClick={() => onRowClick(dep)}>
          <td colSpan={colSpan}><AppriseServerSummaryCell server={dep.server} /></td>
        </tr>
      )}
      </Fragment>
    )
  }

  return (
    <div className={pageStyles.tableWrapper}>
      <table className={pageStyles.table} data-testid="deployments-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <SortHeader key={column.id} column={column} ordering={ordering} onSort={onSort} />
            ))}
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups
            ? groups.map((group) => (
                <Fragment key={group.key}>
                  <tr className={styles.groupRow} data-testid={`customer-group-${group.key}`}>
                    <td colSpan={colSpan}>
                      <div className={styles.groupHeader}>
                        <span className={styles.groupName}>{group.label}</span>
                        {group.customerNumber && <span className={styles.mono}>#{group.customerNumber}</span>}
                        <span className={styles.groupMeta}>
                          {group.rows.length} deployment{group.rows.length === 1 ? '' : 's'}
                        </span>
                        <span className={styles.groupMeta}>{group.environments.join(', ')}</span>
                        {group.hasWorkwise && (
                          <>
                            <span className={styles.groupMeta}>{group.vmCount} VMs</span>
                            <span className={styles.groupMeta}>{formatUsd(group.estMonthlyCost)} / mo</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {group.rows.map(renderRow)}
                </Fragment>
              ))
            : rows.map(renderRow)}
        </tbody>
      </table>
    </div>
  )
}
